import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MailIcon, InfinityIcon, TruckIcon, RotateCcwIcon, ShieldCheckIcon } from 'lucide-react';
import { getLegalDoc, legalDocs, LegalSection } from '../data/legal';
import { media } from '../data/product';
import { SiteFooter } from '../components/SiteFooter';
import { PaymentLogos } from '../components/PaymentLogos';
const promises = [{
  icon: InfinityIcon,
  label: 'Lifetime free replacement'
}, {
  icon: RotateCcwIcon,
  label: '30-day returns, we pay shipping'
}, {
  icon: TruckIcon,
  label: 'Ships from Texas in 1 business day'
}, {
  icon: ShieldCheckIcon,
  label: 'Real person on support'
}];
export function Legal() {
  const {
    slug = ''
  } = useParams();
  const doc = getLegalDoc(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const renderSection = (section: LegalSection) => <section key={section.heading} className="border-t border-white/10 pt-7" data-id="element-1012" data-mp="3s6051aaiagqu">
      <h2 className={section.fine ? 'font-display text-lg font-bold uppercase tracking-wide text-steel-400' : 'font-display text-2xl font-extrabold uppercase leading-tight text-offwhite sm:text-3xl'} data-id="element-1013" data-mp="27kd7gw1col7e">
        {section.heading}
      </h2>
      <div className={`mt-3 grid max-w-3xl ${section.fine ? 'gap-2' : 'gap-3'}`} data-id="element-1014" data-mp="2cjy615clyrjl">
        {section.body.map(paragraph => <p key={paragraph} className={section.fine ? 'text-[12px] leading-relaxed text-steel-500' : 'text-[15px] leading-relaxed text-steel-300 sm:text-base'} data-id="element-1015" data-mp="1ovev0i26c0nt">
            {paragraph}
          </p>)}
      </div>
    </section>;
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1016" data-mp="1645li8selbw8">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink-950/95 backdrop-blur" data-id="element-1017" data-mp="1622o33ox8ghh">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8" data-id="element-1018" data-mp="112yg2gdqgosq">
          <Link to="/" className="shrink-0" data-id="element-1019" data-mp="315he3m25koeq">
            <span className="block font-display text-2xl font-extrabold uppercase leading-none tracking-tight text-offwhite sm:text-3xl" data-id="element-1020" data-mp="365dc94o65lmd">
              Grit<span className="text-grit-500" data-id="element-1021" data-mp="3azhi1klp7nlv">Vue</span>
              <sup className="ml-0.5 align-super text-[9px] text-steel-400" data-id="element-1022" data-mp="3exnlojr8pn2k">™</sup>
            </span>
            <span className="mt-0.5 block text-[8px] font-semibold uppercase tracking-[0.3em] text-steel-400 sm:text-[9px]" data-id="element-1023" data-mp="2i9hjs4lpd8rz">
              See the job through
            </span>
          </Link>
          <Link to="/#offer" className="inline-flex shrink-0 items-center bg-grit-500 px-3.5 py-2.5 font-display text-base font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:px-4 sm:text-lg" data-id="element-1024" data-mp="2r82zlrzsi9p2">
            Get your X1
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8 lg:py-16" data-id="element-1025" data-mp="llf54jyupauf">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1026" data-mp="go55kbhqzisv">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1027" data-mp="7u0h1yq36jgi" />
          Back to the X1
        </Link>

        {!doc ? <div className="mt-8" data-id="element-1028" data-mp="2t2t15ieav73">
            <h1 className="grit-headline text-[clamp(2.25rem,8vw,4rem)] font-extrabold uppercase text-offwhite" data-id="element-1029" data-mp="3u1gy4ple7pm5">
              Page not found
            </h1>
            <p className="mt-4 text-lg text-steel-300" data-id="element-1030" data-mp="3p4grodkmskui">Pick one of our policies below.</p>
          </div> : <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_320px] lg:gap-16" data-id="element-1031" data-mp="1nb0o6g3g3bi4">
            <article data-id="element-1032" data-mp="1n8y1a4xl9yhm">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-grit-500" data-id="element-1033" data-mp="1j9yoxz9j2zg5">{doc.eyebrow}</p>
              <h1 className="grit-headline mt-3 text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase text-offwhite" data-id="element-1034" data-mp="1qcdngaggd6e2">
                {doc.title}
              </h1>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-steel-500" data-id="element-1035" data-mp="1v9m4g7pv74dq">
                Last updated {doc.updated}
              </p>
              <p className="mt-6 max-w-3xl text-lg leading-relaxed text-steel-200" data-id="element-1036" data-mp="1lcherxwt8qs6">{doc.intro}</p>

              <div className="mt-10 grid gap-8" data-id="element-1037" data-mp="17lm54jehm5nj">
                {doc.sections.filter(section => !section.fine).map(renderSection)}
              </div>

              <div className="mt-12 grid gap-px border-2 border-grit-500 bg-white/10 lg:grid-cols-[1.1fr_1fr]" data-id="element-1038" data-mp="xohr56uspght">
                <img src={media.lensSheet} alt="GritVue X1 illuminated work glasses shown with its clear, amber and smoke lenses" className="block h-auto w-full bg-ink-950 object-contain" data-id="element-1039" data-mp="12mrono3zst6l" />
                <div className="flex flex-col justify-center bg-ink-950 p-6 lg:p-8" data-id="element-1040" data-mp="topd4emmui52">
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-grit-500" data-id="element-1041" data-mp="a131ohxys75m">
                    Covered for life from $39.95
                  </p>
                  <p className="grit-headline mt-3 text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold uppercase text-offwhite" data-id="element-1042" data-mp="j2kd3n0ffknz">
                    Still want yours?
                    <br data-id="element-1043" data-mp="szoznny6pi5b" />
                    <span className="text-grit-500" data-id="element-1044" data-mp="syx4ufcevog3">Pick your pack.</span>
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-1045" data-mp="o1dsjkz9xlqs">
                    Every multi-pack ships free with all 3 lenses, the USB-C cable and lifetime free replacement on
                    every unit.
                  </p>
                  <Link to="/#offer" className="mt-6 inline-flex items-center justify-center gap-2 bg-grit-500 px-6 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:text-2xl" data-id="element-1046" data-mp="xsi5yg9awyji">
                    Back to the packs
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1047" data-mp="dedlm6p6nlex" />
                  </Link>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-steel-400" data-id="element-1048" data-mp="i4u1ryke1l4z">
                    Full-Shift Kit: 3 X1 for $74.95 · $24.98 per X1 · Free U.S. shipping
                  </p>
                </div>
              </div>

              {doc.sections.some(section => section.fine) && <div className="mt-10 grid gap-8" data-id="element-1049" data-mp="x238bzi9ivha">
                  {doc.sections.filter(section => section.fine).map(renderSection)}
                </div>}

              <div className="mt-8 flex flex-col gap-4 border border-white/12 bg-ink-900 p-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1050" data-mp="2ytptvljdxy6d">
                <p data-id="element-1051" data-mp="32w2wi12u5fyx">
                  <span className="block font-display text-2xl font-extrabold uppercase leading-none text-grit-500" data-id="element-1052" data-mp="32vl4jgn7hnwt">
                    Still have a question?
                  </span>
                  <a href="mailto:support@gritvue.com" className="mt-2 inline-flex items-center gap-2 text-[15px] font-bold uppercase tracking-wide text-offwhite transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1053" data-mp="2qkabvhj46y88">
                    <MailIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-1054" data-mp="3e9lfd6run6q2" />
                    support@gritvue.com
                  </a>
                </p>
                <Link to="/#offer" className="inline-flex items-center justify-center bg-grit-500 px-6 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px]" data-id="element-1055" data-mp="359pxyk7pafhl">
                  Shop the X1
                </Link>
              </div>
            </article>

            <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1056" data-mp="3owwoja0srs7e">
              <nav aria-label="Policies" className="border border-white/12 bg-ink-900" data-id="element-1057" data-mp="3szb6lrwn6nxm">
                <p className="border-b border-white/10 px-5 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1058" data-mp="3ezryv07qtelu">
                  Policies
                </p>
                <ul data-id="element-1059" data-mp="3k0z6mz9vydsi">
                  {legalDocs.map(item => <li key={item.slug} className="border-b border-white/5 last:border-b-0" data-id="element-1060" data-mp="bke2bvs788vq">
                      <Link to={`/legal/${item.slug}`} className={`block px-5 py-3.5 font-display text-lg font-bold uppercase tracking-wide transition-colors duration-150 ease-grit ${item.slug === doc.slug ? 'bg-grit-500/10 text-grit-500' : 'text-steel-300 hover:text-offwhite'}`} data-id="element-1061" data-mp="ggurmtb4wtae">
                        {item.title}
                      </Link>
                    </li>)}
                </ul>
              </nav>

              <ul className="mt-6 grid gap-3 border border-white/12 bg-ink-900 p-5" data-id="element-1062" data-mp="2ezgc6ivkfgbi">
                {promises.map(promise => {
              const Icon = promise.icon;
              return <li key={promise.label} className="flex items-start gap-3 text-[14px] font-medium text-steel-200" data-id="element-1063" data-mp="2oqkrxkt2f57e">
                      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1064" data-mp="2121j9463sqes" />
                      {promise.label}
                    </li>;
            })}
              </ul>

              <div className="mt-6" data-id="element-1065" data-mp="251s995nt32i9">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-steel-500" data-id="element-1066" data-mp="250qx0xv209yy">Secure checkout</p>
                <div className="mt-2" data-id="element-1067" data-mp="3leh144m6ay8l">
                  <PaymentLogos data-id="element-1068" data-mp="3ggyz6y6kpja3" />
                </div>
              </div>
            </aside>
          </div>}
      </main>

      <SiteFooter data-id="element-1069" data-mp="17ah73pgeec3g" />
    </div>;
}